import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconArrowRight } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { ProductCard } from "../../../products/presentation/components/product-card";
import {
  computeDiscount,
  getStockLabel,
  getWishlistVariantId,
} from "../../../products/utils/card-props";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";
import { useWishlistStore } from "../../../products/domain/stores/useWishlistStore";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useFeaturedDeals } from "../../hooks/useFeaturedDeals";
import type { ProductDto } from "../../../../api";

interface Props {
  /** Show a "View more" link that filters the catalog to Deal of the Week only. */
  showViewMore?: boolean;
  /** Cap number of items rendered (defaults to 4 to match the catalog row width). */
  limit?: number;
  title?: string;
  subtitle?: string;
  className?: string;
}

// Use the same fixed grid template as the catalog product grid
// (products-grid-view.tsx) so each Deal of the Week card keeps the exact same
// size as the rest of the catalog, regardless of how many deals exist. A short
// row left-aligns into the leading columns instead of stretching to fill.
const CATALOG_GRID = "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

export function FeaturedDealsRow({
  showViewMore = false,
  limit = 4,
  title = "Deal of the Week",
  subtitle = "Handpicked offers from verified suppliers",
  className,
}: Props) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useFeaturedDeals();
  const wishlistIds = useWishlistStore((s) => s.wishlistVariantIds);
  const toggleWishlist = useWishlistStore((s) => s.toggle);

  const products = useMemo<ProductDto[]>(
    () =>
      (data ?? [])
        .map((d) => d.product)
        .filter((p): p is ProductDto => p != null)
        .slice(0, limit),
    [data, limit],
  );

  if (isLoading) {
    return (
      <div className={className}>
        <ShimmerLoader message="Loading Deal of the Week…" lines={2} />
      </div>
    );
  }
  if (error || products.length === 0) return null;

  const openDetails = (product: ProductDto) => {
    navigate(getProductDetailsUrl(product.id, product.variantId ?? null));
  };

  return (
    <section className={className}>
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Heading variant="h3" tone="dark">{title}</Heading>
          <Text variant="bodySm" tone="muted">{subtitle}</Text>
        </div>
        {showViewMore && (
          <Link
            to={`${AppRoutes.PRODUCTS}?dealOfWeek=1`}
            className="flex items-center gap-1 text-bodySmBold text-textDark hover:text-brandPrimary"
          >
            View more <IconArrowRight size={16} />
          </Link>
        )}
      </div>

      <div className={`mt-4 grid gap-6 ${CATALOG_GRID}`}>
        {products.map((product) => {
          const discount = computeDiscount(product);
          const variantId = getWishlistVariantId(product);
          return (
            <div
              key={product.id}
              onClick={() => openDetails(product)}
              className="h-full cursor-pointer"
            >
              <ProductCard
                product={product}
                discountLabel={discount ? `${discount}% off` : undefined}
                originalPrice={discount ? (product.standardPrice ?? undefined) : undefined}
                stockLabel={getStockLabel(product)}
                isInWishlist={variantId != null && wishlistIds.includes(variantId)}
                onToggleWishlist={
                  variantId != null ? () => void toggleWishlist(variantId) : undefined
                }
                onViewDetails={() => openDetails(product)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
